'use client';

import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { Loader2 } from 'lucide-react';
import Link from 'next/link';

export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
export type ButtonSize    = 'sm' | 'md' | 'lg' | 'icon';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?:   ButtonVariant;
  size?:      ButtonSize;
  loading?:   boolean;
  leftIcon?:  React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
  /** Renders as a Next.js Link when provided */
  href?:      string;
}

const variantClasses: Record<ButtonVariant, string> = {
  primary:   'bg-primary-500 text-neutral-900 hover:bg-primary-600 active:bg-primary-700 shadow-sm hover:shadow-md',
  secondary: 'bg-secondary-400 text-neutral-900 hover:bg-secondary-500 active:bg-secondary-600 shadow-sm',
  outline:   'border border-(--border) text-(--text) bg-transparent hover:bg-neutral-100 dark:hover:bg-neutral-800 active:bg-neutral-200 dark:active:bg-neutral-700',
  ghost:     'text-(--text) bg-transparent hover:bg-neutral-100 dark:hover:bg-neutral-800 active:bg-neutral-200 dark:active:bg-neutral-700',
  danger:    'bg-danger-500 text-white hover:bg-danger-600 active:bg-danger-700 shadow-sm',
};

const sizeClasses: Record<ButtonSize, string> = {
  sm:   'h-8  px-3  text-xs   gap-1.5 rounded-md',
  md:   'h-10 px-4  text-sm   gap-2   rounded-lg',
  lg:   'h-12 px-6  text-base gap-2.5 rounded-xl',
  icon: 'h-10 w-10 p-0       rounded-lg',
};

function Spinner({ size }: { size: ButtonSize }) {
  const dim = size === 'sm' ? 'h-3.5 w-3.5' : size === 'lg' ? 'h-5 w-5' : 'h-4 w-4';
  return <Loader2 className={`${dim} animate-spin`} aria-hidden="true" />;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant   = 'primary',
      size      = 'md',
      loading   = false,
      leftIcon,
      rightIcon,
      fullWidth = false,
      disabled,
      href,
      className = '',
      children,
      ...rest
    },
    ref
  ) => {
    const isDisabled = disabled || loading;

    const cn = [
      'inline-flex items-center justify-center font-medium cursor-pointer',
      'transition-colors duration-180 select-none no-underline',
      'focus-visible:outline-2 focus-visible:outline-primary-500 focus-visible:outline-offset-2',
      variantClasses[variant],
      sizeClasses[size],
      fullWidth   ? 'w-full' : '',
      isDisabled  ? 'opacity-50 cursor-not-allowed pointer-events-none' : '',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const inner = (
      <>
        {loading ? (
          <Spinner size={size} />
        ) : leftIcon ? (
          <span className="shrink-0 flex items-center" aria-hidden="true">{leftIcon}</span>
        ) : null}

        {size !== 'icon' && children && (
          <span className={loading ? 'sr-only' : ''}>{children}</span>
        )}
        {size === 'icon' && !loading && children}

        {!loading && rightIcon && (
          <span className="shrink-0 flex items-center" aria-hidden="true">{rightIcon}</span>
        )}
      </>
    );

    if (href) {
      return (
        <Link href={href} className={cn} aria-disabled={isDisabled}>
          {inner}
        </Link>
      );
    }

    return (
      <button
        ref={ref}
        disabled={isDisabled}
        aria-disabled={isDisabled}
        aria-busy={loading}
        className={cn}
        {...rest}
      >
        {inner}
      </button>
    );
  }
);

Button.displayName = 'Button';
export default Button;
